import * as React from "react"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgBuilding01 } from "@digits-shared/components/SVGIcons/line/Building01.svg"
import { defined } from "@digits-shared/helpers/filters"
import stringHelper from "@digits-shared/helpers/stringHelper"
import useSession from "@digits-shared/hooks/useSession"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import {
  EmptyMessage,
  EntitiesSearch,
  EntityList,
} from "src/frontend/components/Shared/NavSidebar/Menu/menuStyles"
import {
  EntityListItem,
  type MenuItemsProps,
} from "src/frontend/components/Shared/NavSidebar/Menu/RowItems"
import {
  compareLegalEntityNames,
  matchesLegalEntity,
} from "src/frontend/components/Shared/NavSidebar/Menu/sidebarMenuFunctions"
import type FrontendSession from "src/frontend/session"

/*
  STYLES
*/

const OperatorHeader = styled.div`
  padding: 16px 16px 8px;
  font-weight: ${fonts.weight.heavy};
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
  svg {
    height: 20px;
    width: 20px;
    ${svgPathStyles(colors.secondary, 2)};
  }
`

const SearchWrapper = styled.div`
  padding: 0 16px;
  width: 100%;
  & > * {
    width: 100%;
  }
`

const StyledEntityList = styled(EntityList)`
  // don't override the left/right padding as it is set to dynamically support scroll vs no scroll scenarios
  padding-top: 8px;
  padding-bottom: 8px;
`

/*
  COMPONENTS
*/

export const OperatorItems: React.FC<MenuItemsProps> = ({ onItemClick }) => {
  const { organizations, currentLegalEntity, doppelganger, hasNewOnboardingLegalEntity } =
    useSession<FrontendSession>()
  const [searchTerm, setSearchTerm] = React.useState("")

  const allLegalEntities = React.useMemo(() => {
    const affiliatedEntities = organizations.flatMap((o) =>
      o.affiliations?.map((a) => ({
        legalEntity: a.entity,
        organization: a.organization,
      }))
    )

    const legalEntities = organizations.flatMap((organization) =>
      organization.legalEntities.map((legalEntity) => ({ legalEntity, organization }))
    )

    return [...affiliatedEntities, ...legalEntities].filter(defined).filter(
      ({ legalEntity }) =>
        doppelganger?.hasDashboardAccess ||
        legalEntity.isActive ||
        legalEntity.isApproved ||
        // NOTE: the following check effectively shows the current legal entity in the menu during operator
        // onboarding but will need to be updated once operators adding multiple businesses is supported
        (legalEntity.isNew && hasNewOnboardingLegalEntity)
    )
  }, [organizations, doppelganger?.hasDashboardAccess, hasNewOnboardingLegalEntity])

  const filteredEntities = allLegalEntities
    .filter(({ legalEntity }) => {
      const searchTermRegex = new RegExp(stringHelper.escapeRegExp(searchTerm), "i")
      return searchTerm ? matchesLegalEntity(legalEntity, searchTermRegex) : legalEntity
    })
    .sort(compareLegalEntityNames)

  const showSearch = allLegalEntities.length > 5
  const showHeader = showSearch || allLegalEntities.length > 1

  return (
    <>
      {showHeader && (
        <OperatorHeader>
          <SvgBuilding01 />
          Businesses
        </OperatorHeader>
      )}
      {showSearch && (
        <SearchWrapper>
          <EntitiesSearch
            onChange={setSearchTerm}
            onSubmit={setSearchTerm}
            onClear={setSearchTerm}
            initialValue={searchTerm}
            shouldDebounceOnChange
            placeholder="Find a business"
            stopClickProp
          />
        </SearchWrapper>
      )}

      <StyledEntityList>
        {filteredEntities.map((client) => (
          <EntityListItem
            key={client.legalEntity.id}
            isSelected={currentLegalEntity?.id === client.legalEntity.id}
            organization={client.organization}
            legalEntity={client.legalEntity}
            onItemClick={onItemClick}
          />
        ))}
        {!filteredEntities.length && showSearch && <EmptyMessage>No businesses found</EmptyMessage>}
      </StyledEntityList>
    </>
  )
}
